import { useEffect, useMemo, useState } from 'react';

import { TagMuted } from '@components/core-ui/Tag/variations/TagMuted';
import { IconButtonClean } from '@components/core-ui/form-elements/buttons/IconButton/variations/IconButtonClean';
import ArrowLeftIcon from '@components/icons/ArrowLeft.svg';
import ArrowRightIcon from '@components/icons/ArrowRight.svg';
import { normalizeKeyTerms } from '@helpers/keyTerms/normalizeKeyTerms';
import { TProps } from './types/TProps';

const DEFAULT_PAGE_SIZE = 50;

export const KeyTermsList = ({ keyTerms, onDelete, pageSize = DEFAULT_PAGE_SIZE }: TProps) => {
  const [pageIndex, setPageIndex] = useState(0);
  const normalizedKeyTerms = useMemo(() => normalizeKeyTerms(keyTerms), [keyTerms]);
  const safePageSize = Math.max(1, pageSize);
  const totalPages = Math.max(1, Math.ceil(normalizedKeyTerms.length / safePageSize));
  const pageStart = pageIndex * safePageSize;
  const visibleKeyTerms = normalizedKeyTerms.slice(pageStart, pageStart + safePageSize);
  const shouldShowPagination = normalizedKeyTerms.length > safePageSize;

  useEffect(() => {
    setPageIndex((currentPageIndex) => Math.min(currentPageIndex, totalPages - 1));
  }, [totalPages]);

  const goToPreviousPage = () => setPageIndex((currentPageIndex) => Math.max(0, currentPageIndex - 1));
  const goToNextPage = () => setPageIndex((currentPageIndex) => Math.min(totalPages - 1, currentPageIndex + 1));

  return (
    <div className="flex flex-col gap-3">
      <div className="flex flex-wrap gap-2">
        {visibleKeyTerms.map((keyTerm, index) => {
          const keyTermIndex = pageStart + index;

          return (
            <TagMuted key={`${keyTerm}-${keyTermIndex}`} onDelete={onDelete ? () => onDelete(keyTermIndex) : undefined}>
              {keyTerm}
            </TagMuted>
          );
        })}
      </div>

      {shouldShowPagination && (
        <div className="flex items-center gap-3 text-sm text-gray-500">
          <IconButtonClean title="Previous keyterms page" disabled={pageIndex === 0} onClick={goToPreviousPage}>
            <ArrowLeftIcon className="h-4 w-4" />
          </IconButtonClean>
          <span>
            {pageStart + 1}-{Math.min(pageStart + safePageSize, normalizedKeyTerms.length)} of{' '}
            {normalizedKeyTerms.length}
          </span>
          <IconButtonClean title="Next keyterms page" disabled={pageIndex === totalPages - 1} onClick={goToNextPage}>
            <ArrowRightIcon className="h-4 w-4" />
          </IconButtonClean>
        </div>
      )}
    </div>
  );
};
